import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Trophy, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { saveAbConfig, getAbStats, triggerEvaluateWinner } from "@/lib/ab-test.functions";

export const Route = createFileRoute("/_authenticated/ab-test")({
  component: AbTestPage,
  head: () => ({ meta: [{ title: "A/B-Test Betreffzeilen — InboxPulse" }] }),
});

function AbTestPage() {
  const qc = useQueryClient();
  const saveFn = useServerFn(saveAbConfig);
  const statsFn = useServerFn(getAbStats);
  const evalFn = useServerFn(triggerEvaluateWinner);

  const [selected, setSelected] = useState<string | null>(null);

  const campaigns = useQuery({
    queryKey: ["ab-campaigns"],
    queryFn: async () =>
      (
        await supabase
          .from("campaigns")
          .select(
            "id,name,ab_test_enabled,ab_subject_a,ab_subject_b,ab_split_percent,ab_test_status,ab_winner",
          )
          .order("created_at", { ascending: false })
      ).data ?? [],
  });

  const stats = useQuery({
    queryKey: ["ab-stats", selected],
    queryFn: () => statsFn({ data: { campaign_id: selected! } }),
    enabled: !!selected,
  });

  const mSave = useMutation({
    mutationFn: (vars: {
      campaign_id: string;
      ab_test_enabled: boolean;
      ab_subject_a: string | null;
      ab_subject_b: string | null;
      ab_split_percent: number;
    }) => saveFn({ data: vars }),
    onSuccess: () => {
      toast.success("A/B-Konfiguration gespeichert.");
      qc.invalidateQueries({ queryKey: ["ab-campaigns"] });
      qc.invalidateQueries({ queryKey: ["ab-stats", selected] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mEval = useMutation({
    mutationFn: () => evalFn({ data: { campaign_id: selected! } }),
    onSuccess: (r) => {
      if (r.winner) toast.success(`Gewinner: Variante ${r.winner.toUpperCase()}`);
      else toast.info("Noch zu wenig Daten – mind. 10 Sends je Variante.");
      qc.invalidateQueries({ queryKey: ["ab-campaigns"] });
      qc.invalidateQueries({ queryKey: ["ab-stats", selected] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const current = campaigns.data?.find((c) => c.id === selected) ?? null;

  return (
    <div className="p-8 max-w-6xl space-y-6">
      <PageHeader
        title="A/B-Test Betreffzeilen"
        description="Sende 10 % Variante A, 10 % Variante B – die restlichen 80 % bekommen den Gewinner mit höherer Öffnungsrate."
      />

      <HelpPanel
        title="Hilfe: Zwei Betreffzeilen vergleichen (A/B-Test)"
        what={
          <>
            Du schreibst <b>zwei Versionen</b> derselben Mail (meist nur unterschiedliche
            Betreffzeilen). Beide gehen an je 10% deiner Liste. Welche besser geöffnet wird, bekommt
            die restlichen 80%.
          </>
        }
        why={
          <>
            So sendest du immer den stärkeren Betreff an den Großteil. Statt zu raten, weißt du nach
            1-2 Stunden, was wirkt.
          </>
        }
        how={[
          <>
            <b>Kampagne auswählen</b> links — sie muss bereits zwei Varianten haben.
          </>,
          <>System verteilt automatisch: 10% Variante A, 10% Variante B.</>,
          <>
            Nach 60-90 Minuten klickst du <b>Gewinner ermitteln</b> — restliche 80% bekommen den
            besseren Betreff.
          </>,
          <>Statistik (Öffnungs-Rate je Variante) siehst du live in der Tabelle.</>,
        ]}
        tips={[
          <>Mindestens 200 Empfänger pro Variante, sonst ist das Ergebnis Zufall.</>,
          <>
            Nur EINE Sache pro Test ändern (z.B. nur Betreff), sonst weißt du nicht, was den
            Unterschied gemacht hat.
          </>,
          <>
            Frage gegen Aussage testen (z.B. "Schon gehört?" vs. "3 Tipps für mehr Umsatz") bringt
            oft große Unterschiede.
          </>,
        ]}
        glossary={[
          { term: "Variante", def: "Eine Version der Mail. A = die erste, B = die zweite." },
          {
            term: "Öffnungs-Rate",
            def: "Prozent der Empfänger, die die Mail geöffnet haben. 20%+ = gut.",
          },
          {
            term: "Statistische Signifikanz",
            def: "Heißt: Ergebnis ist verlässlich, kein Zufall. Braucht genug Empfänger.",
          },
        ]}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 md:col-span-1">
          <div className="text-xs text-muted-foreground mb-2">Kampagne wählen</div>
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {campaigns.data?.length === 0 && (
              <div className="text-sm text-muted-foreground">Keine Kampagnen.</div>
            )}
            {campaigns.data?.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelected(c.id)}
                className={`w-full text-left text-sm px-3 py-2 rounded transition ${
                  selected === c.id
                    ? "bg-primary/15 text-foreground"
                    : "hover:bg-muted text-muted-foreground"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{c.name}</span>
                  {c.ab_test_enabled && (
                    <Badge variant="secondary" className="text-[10px]">
                      A/B
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>

        <div className="md:col-span-2 space-y-4">
          {!current ? (
            <Card className="p-12 text-center bg-card/40 border-dashed">
              <FlaskConical className="size-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Kampagne wählen</p>
            </Card>
          ) : (
            <AbEditor
              key={current.id}
              campaign={current}
              stats={stats.data}
              onSave={(v) => mSave.mutate(v)}
              onEvaluate={() => mEval.mutate()}
              saving={mSave.isPending}
              evaluating={mEval.isPending}
            />
          )}
        </div>
      </div>
    </div>
  );
}

type Campaign = {
  id: string;
  name: string;
  ab_test_enabled: boolean;
  ab_subject_a: string | null;
  ab_subject_b: string | null;
  ab_split_percent: number;
  ab_test_status: string;
  ab_winner: string | null;
};

type StatsRow = { sent: number; opened: number };
type StatsData =
  | {
      campaign: Campaign & { ab_winner_selected_at: string | null };
      stats: Record<"a" | "b" | "winner", StatsRow>;
    }
  | undefined;

function AbEditor({
  campaign,
  stats,
  onSave,
  onEvaluate,
  saving,
  evaluating,
}: {
  campaign: Campaign;
  stats: StatsData;
  onSave: (v: {
    campaign_id: string;
    ab_test_enabled: boolean;
    ab_subject_a: string | null;
    ab_subject_b: string | null;
    ab_split_percent: number;
  }) => void;
  onEvaluate: () => void;
  saving: boolean;
  evaluating: boolean;
}) {
  const [enabled, setEnabled] = useState(campaign.ab_test_enabled);
  const [a, setA] = useState(campaign.ab_subject_a ?? "");
  const [b, setB] = useState(campaign.ab_subject_b ?? "");
  const [split, setSplit] = useState(campaign.ab_split_percent || 10);

  const rate = (s: StatsRow) => (s.sent > 0 ? ((s.opened / s.sent) * 100).toFixed(1) + "%" : "–");

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{campaign.name}</h2>
        <label className="flex items-center gap-2 text-sm">
          A/B aktiv
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </label>
      </div>

      <div>
        <Label className="text-xs">Variante A – Betreff</Label>
        <Input
          value={a}
          onChange={(e) => setA(e.target.value)}
          placeholder="z. B. Großer Rabatt heute!"
        />
      </div>
      <div>
        <Label className="text-xs">Variante B – Betreff</Label>
        <Input
          value={b}
          onChange={(e) => setB(e.target.value)}
          placeholder="z. B. Nur heute: bis 50 % sparen"
        />
      </div>
      <div>
        <Label className="text-xs">Split-Anteil je Variante (%)</Label>
        <Input
          type="number"
          min={1}
          max={50}
          value={split}
          onChange={(e) => setSplit(Number(e.target.value))}
        />
        <div className="text-xs text-muted-foreground mt-1">
          Empfehlung: 10 % A + 10 % B → 80 % Gewinner.
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() =>
            onSave({
              campaign_id: campaign.id,
              ab_test_enabled: enabled,
              ab_subject_a: a || null,
              ab_subject_b: b || null,
              ab_split_percent: split,
            })
          }
          disabled={saving}
        >
          Speichern
        </Button>
        <Button variant="outline" onClick={onEvaluate} disabled={evaluating}>
          <RefreshCw className={`size-4 mr-1 ${evaluating ? "animate-spin" : ""}`} />
          Gewinner jetzt bewerten
        </Button>
      </div>

      {stats && (
        <div className="pt-2 border-t border-border space-y-2">
          <div className="text-sm font-medium flex items-center gap-2">
            Ergebnisse
            {campaign.ab_winner && (
              <Badge>
                <Trophy className="size-3 mr-1" />
                Gewinner: {campaign.ab_winner.toUpperCase()}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            {(["a", "b", "winner"] as const).map((k) => (
              <div key={k} className="border border-border rounded p-3">
                <div className="text-xs text-muted-foreground uppercase">
                  {k === "winner" ? "Gewinner-Phase" : `Variante ${k.toUpperCase()}`}
                </div>
                <div className="text-lg font-semibold">{rate(stats.stats[k])}</div>
                <div className="text-xs text-muted-foreground">
                  {stats.stats[k].opened} / {stats.stats[k].sent} Öffnungen
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
